import { extendTheme } from '@chakra-ui/react';

const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },

  colors: {
    brand: {
      50: '#EAF7F0',
      100: '#CDEBDB',
      200: '#9BD8B8',
      300: '#63BF91',
      400: '#33A56F',
      500: '#148A55',
      600: '#0E7046',
      700: '#0B593A',
      800: '#08422B',
      900: '#052A1B',
    },
    gold: {
      50: '#FDF8EC',
      100: '#F9EDCE',
      200: '#F2DA9E',
      300: '#E9C468',
      400: '#E0AE38',
      500: '#C9931F',
      600: '#A67514',
      700: '#7E570F',
      800: '#55390B',
      900: '#2E1F07',
    },
    navy: {
      50: '#EEF3FA',
      100: '#D6E2F0',
      200: '#AFC6E0',
      300: '#7FA3CC',
      400: '#527FB5',
      500: '#2F6299',
      600: '#1F4E81',
      700: '#163C68',
      800: '#0F2C4D',
      900: '#081C34',
    },
  },

  fonts: {
    heading: "'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },

  fontSizes: {
    xs: '0.8125rem',
    sm: '0.9375rem',
    md: '1.0625rem',
    lg: '1.1875rem',
    xl: '1.375rem',
    '2xl': '1.75rem',
    '3xl': '2.25rem',
    '4xl': '2.875rem',
    '5xl': '3.5rem',
    '6xl': '4.25rem',
  },

  shadows: {
    soft: '0 1px 2px rgba(8, 28, 52, 0.04), 0 8px 24px -8px rgba(8, 28, 52, 0.10)',
    card: '0 2px 4px rgba(8, 28, 52, 0.04), 0 16px 40px -16px rgba(8, 28, 52, 0.18)',
    lift: '0 8px 16px -8px rgba(8, 28, 52, 0.12), 0 32px 64px -24px rgba(8, 28, 52, 0.28)',
    glow: '0 10px 30px -8px rgba(20, 138, 85, 0.45)',
    glowGold: '0 10px 30px -8px rgba(201, 147, 31, 0.45)',
    glowNavy: '0 10px 30px -8px rgba(31, 78, 129, 0.45)',
    inner: 'inset 0 1px 0 rgba(255, 255, 255, 0.08)',
  },

  radii: {
    sm: '6px',
    md: '10px',
    lg: '14px',
    xl: '20px',
    '2xl': '28px',
    '3xl': '36px',
  },

  styles: {
    global: {
      'html, body': {
        scrollBehavior: 'smooth',
        scrollPaddingTop: '96px',
      },
      body: {
        bg: 'white',
        color: 'gray.800',
        lineHeight: '1.7',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      'h1, h2, h3, h4, h5, h6': {
        overflowWrap: 'break-word',
        wordBreak: 'normal',
        hyphens: 'auto',
      },
      '::selection': {
        bg: 'brand.500',
        color: 'white',
      },
      '*::-webkit-scrollbar': {
        width: '10px',
        height: '10px',
      },
      '*::-webkit-scrollbar-track': {
        bg: 'gray.100',
      },
      '*::-webkit-scrollbar-thumb': {
        bg: 'brand.600',
        borderRadius: 'full',
        border: '2px solid',
        borderColor: 'gray.100',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        bg: 'brand.700',
      },
      '*:focus-visible': {
        outline: '2px solid',
        outlineColor: 'brand.400',
        outlineOffset: '3px',
      },
    },
  },

  components: {
    Button: {
      baseStyle: {
        fontWeight: '600',
        letterSpacing: '-0.01em',
        borderRadius: 'xl',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        _active: { transform: 'scale(0.98)' },
        _focusVisible: { boxShadow: '0 0 0 3px rgba(20, 138, 85, 0.35)' },
      },
      sizes: {
        md: { h: '44px', px: 6, fontSize: 'sm' },
        lg: { h: '54px', px: 8, fontSize: 'md' },
      },
      variants: {
        solid: {
          bg: 'brand.500',
          color: 'white',
          _hover: { bg: 'brand.600', transform: 'translateY(-2px)', boxShadow: 'glow' },
          _active: { bg: 'brand.700', transform: 'translateY(0)' },
        },
        outline: {
          borderColor: 'currentColor',
          _hover: { transform: 'translateY(-2px)' },
        },
        ghost: {
          _hover: { bg: 'blackAlpha.50' },
        },
        ghostOnDark: {
          color: 'white',
          border: '1px solid',
          borderColor: 'whiteAlpha.400',
          bg: 'whiteAlpha.100',
          backdropFilter: 'blur(8px)',
          _hover: { bg: 'whiteAlpha.300', borderColor: 'whiteAlpha.700', transform: 'translateY(-2px)' },
        },
      },
      defaultProps: {
        variant: 'solid',
      },
    },

    Heading: {
      baseStyle: {
        fontWeight: '800',
        letterSpacing: '-0.03em',
        lineHeight: '1.12',
        color: 'gray.900',
      },
    },

    Text: {
      baseStyle: {
        letterSpacing: '-0.005em',
      },
    },

    Badge: {
      baseStyle: {
        textTransform: 'none',
        fontWeight: '600',
        letterSpacing: '0.01em',
        borderRadius: 'full',
      },
    },

    Container: {
      baseStyle: {
        px: { base: 5, md: 8 },
      },
    },

    Link: {
      baseStyle: {
        _hover: { textDecoration: 'none' },
      },
    },
  },
});

export default theme;
